import { useEffect, useId, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, ChevronDown, ChevronRight, Building2, Receipt, Globe, UserCircle, FileText, Search, Loader2 } from 'lucide-react'
import { apiClient } from '@/services/api'
import { cn } from '@/lib/utils'
import type { Client, ClientStatus } from '@/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ClientFormProps {
  client?: Client
  onClose: () => void
  onSuccess?: (client: Client) => void
  /** Mostra solo i campi essenziali (nome, email, telefono) */
  compact?: boolean
}

interface ClientFormData {
  // Generali
  name: string
  status: ClientStatus
  email: string
  phone: string
  website: string
  logo: string
  industry: string
  companySize: string
  notes: string
  // Fatturazione
  legalName: string
  vatNumber: string
  fiscalCode: string
  sdiCode: string
  pecEmail: string
  iban: string
  address: string
  billingAddress: string
  city: string
  province: string
  zipCode: string
  country: string
  // Social
  linkedinUrl: string
  facebookUrl: string
  instagramUrl: string
  tiktokUrl: string
  youtubeUrl: string
  xUrl: string
  // Referente
  contactName: string
  contactRole: string
  contactPhone: string
  contactEmail: string
}

const INITIAL_DATA: ClientFormData = {
  name: '',
  status: 'ACTIVE',
  email: '',
  phone: '',
  website: '',
  logo: '',
  industry: '',
  companySize: '',
  notes: '',
  legalName: '',
  vatNumber: '',
  fiscalCode: '',
  sdiCode: '',
  pecEmail: '',
  iban: '',
  address: '',
  billingAddress: '',
  city: '',
  province: '',
  zipCode: '',
  country: 'Italia',
  linkedinUrl: '',
  facebookUrl: '',
  instagramUrl: '',
  tiktokUrl: '',
  youtubeUrl: '',
  xUrl: '',
  contactName: '',
  contactRole: '',
  contactPhone: '',
  contactEmail: '',
}

const STATUS_OPTIONS: { value: ClientStatus; label: string }[] = [
  { value: 'PROSPECT', label: 'Prospect' },
  { value: 'ACTIVE', label: 'Attivo' },
  { value: 'INACTIVE', label: 'Inattivo' },
  { value: 'CHURNED', label: 'Perso' },
]

const COMPANY_SIZE_OPTIONS = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '500+',
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function FormField({
  label,
  required,
  children,
  error,
  hint,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
  error?: string | undefined
  hint?: string | undefined
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1.5">
        {label}
        {required && <span className="text-danger-600 ml-0.5">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
      {error && <p className="text-xs text-danger-600 mt-1">{error}</p>}
    </div>
  )
}

function Section({
  title,
  icon: Icon,
  children,
  defaultOpen = false,
}: {
  title: string
  icon: React.ElementType
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2.5 px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
      >
        <Icon className="w-4 h-4 text-slate-500" />
        <span className="text-sm font-medium text-slate-700 flex-1">{title}</span>
        {open
          ? <ChevronDown className="w-4 h-4 text-slate-400" />
          : <ChevronRight className="w-4 h-4 text-slate-400" />
        }
      </button>
      {open && <div className="px-4 py-4 space-y-4 border-t border-slate-200">{children}</div>}
    </div>
  )
}

const inputClass = cn(
  'w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg',
  'focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20',
  'placeholder:text-slate-400 transition-colors'
)

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ClientForm({ client, onClose, onSuccess, compact }: ClientFormProps) {
  const queryClient = useQueryClient()
  const isEdit = Boolean(client)
  const titleId = useId()

  // Chiusura con ESC
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const [formData, setFormData] = useState<ClientFormData>(() => {
    if (client) {
      const c = client as any
      return {
        name: c.name ?? '',
        status: c.status ?? 'ACTIVE',
        email: c.email ?? '',
        phone: c.phone ?? '',
        website: c.website ?? '',
        logo: c.logo ?? '',
        industry: c.industry ?? '',
        companySize: c.companySize ?? '',
        notes: c.notes ?? '',
        legalName: c.legalName ?? '',
        vatNumber: c.vatNumber ?? '',
        fiscalCode: c.fiscalCode ?? '',
        sdiCode: c.sdiCode ?? '',
        pecEmail: c.pecEmail ?? '',
        iban: c.iban ?? '',
        address: c.address ?? '',
        billingAddress: c.billingAddress ?? '',
        city: c.city ?? '',
        province: c.province ?? '',
        zipCode: c.zipCode ?? '',
        country: c.country ?? 'Italia',
        linkedinUrl: c.linkedinUrl ?? '',
        facebookUrl: c.facebookUrl ?? '',
        instagramUrl: c.instagramUrl ?? '',
        tiktokUrl: c.tiktokUrl ?? '',
        youtubeUrl: c.youtubeUrl ?? '',
        xUrl: c.xUrl ?? '',
        contactName: c.contactName ?? '',
        contactRole: c.contactRole ?? '',
        contactPhone: c.contactPhone ?? '',
        contactEmail: c.contactEmail ?? '',
      }
    }
    return INITIAL_DATA
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [vatLoading, setVatLoading] = useState(false)
  const [vatMessage, setVatMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null)

  async function lookupVat() {
    const vat = formData.vatNumber.trim()
    if (!vat) {
      setVatMessage({ type: 'error', text: 'Inserisci la Partita IVA prima di cercare' })
      return
    }
    setVatLoading(true)
    setVatMessage(null)
    try {
      const result = await apiClient.get<{
        legalName: string
        address: string
        city: string
        province: string
        zipCode: string
        country: string
        vatNumber: string
      }>(`/clients/lookup-vat/${encodeURIComponent(vat)}`)
      const d = result as any
      const data = d.data ?? d
      setFormData((prev) => ({
        ...prev,
        legalName: data.legalName || prev.legalName,
        address: data.address || prev.address,
        city: data.city || prev.city,
        province: data.province || prev.province,
        zipCode: data.zipCode || prev.zipCode,
        country: data.country || prev.country,
        vatNumber: data.vatNumber || prev.vatNumber,
      }))
      setVatMessage({ type: 'success', text: 'Dati recuperati con successo' })
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 404 || status === 502) {
        setVatMessage({ type: 'warning', text: 'P.IVA non trovata nel registro europeo VIES. Potrebbe non essere registrata per scambi UE — compila i dati manualmente.' })
      } else {
        const msg = err?.response?.data?.error?.message ?? err?.response?.data?.message ?? 'Errore nella ricerca'
        setVatMessage({ type: 'error', text: msg })
      }
    } finally {
      setVatLoading(false)
    }
  }

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      isEdit
        ? apiClient.put<any>(`/clients/${client!.id}`, data)
        : apiClient.post<any>('/clients', data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      if (result.pendingApproval) {
        alert(result.message || 'Modifica inviata per approvazione')
        onClose()
      } else {
        onSuccess?.(result.data ?? result)
        onClose()
      }
    },
  })

  function validate(): boolean {
    const newErrors: Record<string, string> = {}
    if (!formData.name.trim()) newErrors.name = 'Il nome del cliente è obbligatorio'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return

    // Build payload, omitting empty strings
    const payload: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(formData)) {
      if (typeof val === 'string' && val.trim() === '') continue
      payload[key] = val
    }
    // Always send name
    payload.name = formData.name.trim()

    mutation.mutate(payload)
  }

  function handleChange(field: keyof ClientFormData, value: string) {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      const next = { ...errors }
      delete next[field]
      setErrors(next)
    }
  }

  // =========================================================================
  // Compact mode: only name + email + phone
  // =========================================================================
  if (compact) {
    return (
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="bg-white rounded-3xl shadow-modal w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div>
              <h2 id={titleId} className="text-base font-semibold text-slate-900">Nuovo Cliente</h2>
              <p className="text-xs text-slate-500 mt-0.5">Crea rapidamente un nuovo cliente</p>
            </div>
            <button type="button" aria-label="Chiudi" onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
            <div className="px-6 py-4 space-y-4">
              <FormField label="Nome Cliente" required error={errors.name}>
                <input type="text" className={inputClass} value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="es. Azienda Srl" autoFocus />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Email">
                  <input type="email" className={inputClass} value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="info@azienda.it" />
                </FormField>
                <FormField label="Telefono">
                  <input type="tel" className={inputClass} value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="+39 02 1234567" />
                </FormField>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">Annulla</button>
              <button type="submit" disabled={mutation.isPending} className={cn('px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed')}>
                {mutation.isPending ? 'Salvataggio...' : 'Crea Cliente'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // =========================================================================
  // Full mode: all fields with collapsible sections
  // =========================================================================
  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-3xl shadow-modal w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <h2 id={titleId} className="text-base font-semibold text-slate-900">
              {isEdit ? 'Modifica Cliente' : 'Nuovo Cliente'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {isEdit ? 'Aggiorna le informazioni del cliente' : 'Solo il nome è obbligatorio. Puoi completare il resto in seguito.'}
            </p>
          </div>
          <button type="button" aria-label="Chiudi" onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">

            {/* Global error */}
            {mutation.isError && (
              <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                <span className="text-xs text-danger-700">{(mutation.error as any)?.message ?? 'Errore durante il salvataggio'}</span>
              </div>
            )}

            {/* ---- INFORMAZIONI GENERALI (sempre visibile) ---- */}
            <div className="space-y-4">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Informazioni Generali</h3>

              <FormField label="Nome Cliente / Brand" required error={errors.name}>
                <input type="text" className={inputClass} value={formData.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="es. Motorsport Italia SpA" autoFocus />
              </FormField>

              <div className="grid grid-cols-2 gap-3">
                <FormField label="Stato">
                  <select className={inputClass} value={formData.status} onChange={(e) => handleChange('status', e.target.value as ClientStatus)}>
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Settore">
                  <input type="text" className={inputClass} value={formData.industry} onChange={(e) => handleChange('industry', e.target.value)} placeholder="es. Automotive" />
                </FormField>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <FormField label="Email">
                  <input type="email" className={inputClass} value={formData.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="info@azienda.it" />
                </FormField>
                <FormField label="Telefono">
                  <input type="tel" className={inputClass} value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="+39 02 1234567" />
                </FormField>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <FormField label="Sito Web">
                  <input type="url" className={inputClass} value={formData.website} onChange={(e) => handleChange('website', e.target.value)} placeholder="https://www.azienda.it" />
                </FormField>
                <FormField label="Dimensione Azienda">
                  <select className={inputClass} value={formData.companySize} onChange={(e) => handleChange('companySize', e.target.value)}>
                    <option value="">Seleziona...</option>
                    {COMPANY_SIZE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s} dipendenti</option>
                    ))}
                  </select>
                </FormField>
              </div>

              <FormField label="Logo" hint="URL dell'immagine del logo del cliente">
                <input type="url" className={inputClass} value={formData.logo} onChange={(e) => handleChange('logo', e.target.value)} placeholder="https://..." />
              </FormField>
            </div>

            {/* ---- SEZIONE: DATI FATTURAZIONE ---- */}
            <Section title="Dati Fatturazione" icon={Receipt} defaultOpen={isEdit}>
              <FormField label="Ragione Sociale">
                <input type="text" className={inputClass} value={formData.legalName} onChange={(e) => handleChange('legalName', e.target.value)} placeholder="Ragione sociale completa" />
              </FormField>

              <div className="grid grid-cols-2 gap-3">
                <FormField label="Partita IVA" hint="Inserisci e premi Cerca per compilare automaticamente">
                  <div className="flex gap-2">
                    <input type="text" className={cn(inputClass, 'flex-1')} value={formData.vatNumber} onChange={(e) => handleChange('vatNumber', e.target.value)} placeholder="IT12345678901" />
                    <button
                      type="button"
                      onClick={lookupVat}
                      disabled={vatLoading || !formData.vatNumber.trim()}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition-colors whitespace-nowrap',
                        'bg-primary-50 text-primary-600 border border-primary-200 hover:bg-primary-100',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      )}
                    >
                      {vatLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                      Cerca
                    </button>
                  </div>
                  {vatMessage && (
                    <p className={cn('text-[11px] mt-1', vatMessage.type === 'success' ? 'text-success-600' : vatMessage.type === 'warning' ? 'text-warning-600' : 'text-danger-600')}>
                      {vatMessage.text}
                    </p>
                  )}
                </FormField>
                <FormField label="Codice Fiscale">
                  <input type="text" className={inputClass} value={formData.fiscalCode} onChange={(e) => handleChange('fiscalCode', e.target.value)} placeholder="12345678901" />
                </FormField>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <FormField label="Codice SDI" hint="Per fatturazione elettronica">
                  <input type="text" className={inputClass} value={formData.sdiCode} onChange={(e) => handleChange('sdiCode', e.target.value)} placeholder="XXXXXXX" />
                </FormField>
                <FormField label="PEC">
                  <input type="email" className={inputClass} value={formData.pecEmail} onChange={(e) => handleChange('pecEmail', e.target.value)} placeholder="azienda@pec.it" />
                </FormField>
              </div>

              <FormField label="IBAN">
                <input type="text" className={inputClass} value={formData.iban} onChange={(e) => handleChange('iban', e.target.value)} placeholder="IT60X0542811101000000123456" />
              </FormField>

              <FormField label="Sede Legale">
                <input type="text" className={inputClass} value={formData.address} onChange={(e) => handleChange('address', e.target.value)} placeholder="Via Roma 1" />
              </FormField>

              <div className="grid grid-cols-3 gap-3">
                <FormField label="Città">
                  <input type="text" className={inputClass} value={formData.city} onChange={(e) => handleChange('city', e.target.value)} placeholder="Milano" />
                </FormField>
                <FormField label="Provincia">
                  <input type="text" className={inputClass} value={formData.province} onChange={(e) => handleChange('province', e.target.value)} placeholder="MI" maxLength={2} />
                </FormField>
                <FormField label="CAP">
                  <input type="text" className={inputClass} value={formData.zipCode} onChange={(e) => handleChange('zipCode', e.target.value)} placeholder="20100" maxLength={5} />
                </FormField>
              </div>

              <FormField label="Paese">
                <input type="text" className={inputClass} value={formData.country} onChange={(e) => handleChange('country', e.target.value)} placeholder="Italia" />
              </FormField>

              <FormField label="Indirizzo di Fatturazione" hint="Se diverso dalla sede legale">
                <input type="text" className={inputClass} value={formData.billingAddress} onChange={(e) => handleChange('billingAddress', e.target.value)} placeholder="Via..." />
              </FormField>

            </Section>

            {/* ---- SEZIONE: PROFILI SOCIAL ---- */}
            <Section title="Profili Social" icon={Globe}>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="LinkedIn">
                  <input type="url" className={inputClass} value={formData.linkedinUrl} onChange={(e) => handleChange('linkedinUrl', e.target.value)} placeholder="https://linkedin.com/company/..." />
                </FormField>
                <FormField label="Facebook">
                  <input type="url" className={inputClass} value={formData.facebookUrl} onChange={(e) => handleChange('facebookUrl', e.target.value)} placeholder="https://facebook.com/..." />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Instagram">
                  <input type="url" className={inputClass} value={formData.instagramUrl} onChange={(e) => handleChange('instagramUrl', e.target.value)} placeholder="https://instagram.com/..." />
                </FormField>
                <FormField label="TikTok">
                  <input type="url" className={inputClass} value={formData.tiktokUrl} onChange={(e) => handleChange('tiktokUrl', e.target.value)} placeholder="https://tiktok.com/@..." />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="YouTube">
                  <input type="url" className={inputClass} value={formData.youtubeUrl} onChange={(e) => handleChange('youtubeUrl', e.target.value)} placeholder="https://youtube.com/@..." />
                </FormField>
                <FormField label="X (Twitter)">
                  <input type="url" className={inputClass} value={formData.xUrl} onChange={(e) => handleChange('xUrl', e.target.value)} placeholder="https://x.com/..." />
                </FormField>
              </div>
            </Section>

            {/* ---- SEZIONE: REFERENTE PRINCIPALE ---- */}
            <Section title="Referente Principale" icon={UserCircle}>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Nome e Cognome">
                  <input type="text" className={inputClass} value={formData.contactName} onChange={(e) => handleChange('contactName', e.target.value)} placeholder="Mario Rossi" />
                </FormField>
                <FormField label="Ruolo">
                  <input type="text" className={inputClass} value={formData.contactRole} onChange={(e) => handleChange('contactRole', e.target.value)} placeholder="es. Marketing Manager" />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Email Referente">
                  <input type="email" className={inputClass} value={formData.contactEmail} onChange={(e) => handleChange('contactEmail', e.target.value)} placeholder="mario.rossi@azienda.it" />
                </FormField>
                <FormField label="Telefono Referente">
                  <input type="tel" className={inputClass} value={formData.contactPhone} onChange={(e) => handleChange('contactPhone', e.target.value)} placeholder="+39 333 1234567" />
                </FormField>
              </div>
            </Section>

            {/* ---- SEZIONE: NOTE ---- */}
            <Section title="Note Interne" icon={FileText}>
              <FormField label="Note">
                <textarea className={cn(inputClass, 'resize-none')} rows={4} value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} placeholder="Informazioni aggiuntive, accordi particolari, storico relazione..." />
              </FormField>
            </Section>

          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
            <p className="text-xs text-slate-400">
              Solo il <span className="font-medium text-slate-600">nome</span> è obbligatorio
            </p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                Annulla
              </button>
              <button
                type="submit"
                disabled={mutation.isPending}
                className={cn(
                  'px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg',
                  'hover:bg-primary-700 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {mutation.isPending
                  ? 'Salvataggio...'
                  : isEdit
                  ? 'Salva Modifiche'
                  : 'Crea Cliente'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
